import { HOUR_MS } from '@apm/contracts';
import { fromLocalInput, toLocalInput } from '../time';
import { Field, TextInput } from './Field';

/**
 * 조회 기간 (19회차, 후보 #17·C-17).
 *
 * 16회차까지 알림·DB 사건은 "최근 1시간/하루/7일" 고정 목록만 있었고, 그 창마저
 * **화면에 들어온 시각**에 묶여 있어 새로고침을 눌러도 옛 구간을 다시 읽었다.
 * 여기서는 프리셋을 누르면 그 순간의 시각으로 시작·끝을 다시 계산한다.
 * 시작·끝을 직접 고치면 그 값이 이긴다.
 *
 * 값은 `datetime-local`이라 **브라우저 로컬 시각 문자열**이다. epoch ms로 바꾸는 것은
 * `time.ts`가 한 곳에서 한다.
 */

export interface Range {
  /** `YYYY-MM-DDTHH:mm` */
  from: string;
  to: string;
}

export interface Preset {
  label: string;
  hours: number;
}

export const DEFAULT_PRESETS: readonly Preset[] = [
  { label: '최근 1시간', hours: 1 },
  { label: '최근 6시간', hours: 6 },
  { label: '최근 하루', hours: 24 },
  { label: '최근 7일', hours: 24 * 7 },
];

/** 지금을 끝으로 하는 기간 */
export function rangeOf(hours: number, now: number = Date.now()): Range {
  return { from: toLocalInput(now - hours * HOUR_MS), to: toLocalInput(now) };
}

/** 화면 값 → 서버가 받는 epoch ms 한 쌍. 비었으면 undefined */
export function rangeMs(range: Range): { from?: number; to?: number } {
  return { from: fromLocalInput(range.from), to: fromLocalInput(range.to) };
}

/**
 * 주소의 쿼리스트링(`from=<epoch ms>&to=<epoch ms>`) → 기간. 둘 다 숫자여야 한다 (22-B회차).
 * 통계·진단이 같은 모양으로 기간을 주소에 싣는다. 없거나 모양이 틀리면 undefined
 */
export function rangeFromQuery(query: string | undefined): Range | undefined {
  if (query === undefined || query === '') return undefined;
  const params = new URLSearchParams(query);
  const from = params.get('from');
  const to = params.get('to');
  if (from === null || to === null) return undefined;
  const fromMs = Number(from);
  const toMs = Number(to);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return undefined;
  return { from: toLocalInput(fromMs), to: toLocalInput(toMs) };
}

/** 기간 → 주소에 실을 `from`·`to`. 비어 있는 쪽은 싣지 않는다 */
export function rangeParams(range: Range, params: URLSearchParams = new URLSearchParams()) {
  const bounds = rangeMs(range);
  if (bounds.from !== undefined) params.set('from', String(bounds.from));
  if (bounds.to !== undefined) params.set('to', String(bounds.to));
  return params;
}

export function RangePicker({
  idPrefix,
  value,
  onChange,
  presets = DEFAULT_PRESETS,
}: {
  /** 한 화면에 둘 이상 놓일 수 있어 id를 밖에서 받는다 */
  idPrefix: string;
  value: Range;
  onChange: (next: Range) => void;
  presets?: readonly Preset[];
}) {
  return (
    <div className="flex flex-wrap items-end gap-4">
      <Field label="시작" htmlFor={`${idPrefix}-from`}>
        <TextInput
          id={`${idPrefix}-from`}
          type="datetime-local"
          value={value.from}
          onChange={(from) => onChange({ ...value, from })}
        />
      </Field>
      <Field label="끝" htmlFor={`${idPrefix}-to`}>
        <TextInput
          id={`${idPrefix}-to`}
          type="datetime-local"
          value={value.to}
          onChange={(to) => onChange({ ...value, to })}
        />
      </Field>
      <div className="flex flex-wrap gap-1.5 pb-1.5">
        {presets.map((preset) => (
          <button
            key={preset.label}
            type="button"
            onClick={() => onChange(rangeOf(preset.hours))}
            className="rounded-md border border-line bg-panel px-2.5 py-1 text-xs text-muted transition-colors duration-150 hover:bg-band hover:text-ink"
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  );
}
